/**
 * Agent Onboarding Component
 *
 * "Employee onboarding" experience for AI agents.
 * Presents resources/integrations in human-friendly terms:
 * - "Give them a phone" instead of "Configure SendBlue MCP"
 * - "Give them a computer" instead of "Set up Kernel.sh"
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme, raxButtonStyles } from "../styles/rax-theme.js";

export type ResourceType =
  | "phone_sendblue"
  | "email_resend"
  | "computer_kernel"
  | "skills_github"
  | "notes_notion"
  | "calendar_google"
  | "slack"
  | "discord";

export type ResourceStatus = "not_connected" | "connected" | "error" | "connecting";

export interface ResourceConfig {
  type: ResourceType;
  status: ResourceStatus;
  connectedAt?: string;
  config?: Record<string, unknown>;
  errorMessage?: string;
}

export interface Playbook {
  id: string;
  name: string;
  icon: string;
  description: string;
  integrations: ResourceType[];
  suggestedPersona?: string;
}

// Re-export for backwards compatibility
export type OnboardingPlaybook = Playbook;

/**
 * Preset playbooks for common agent roles.
 * These bundles provide quick setup for typical use cases.
 */
export const PRESET_PLAYBOOKS: Playbook[] = [
  {
    id: "researcher",
    name: "Researcher",
    icon: "🔬",
    description: "Browse the web, search repos, gather information",
    integrations: ["computer_kernel", "skills_github"],
    suggestedPersona: "A meticulous research assistant who excels at finding and synthesizing information from multiple sources.",
  },
  {
    id: "writer",
    name: "Writer",
    icon: "✍️",
    description: "Draft content, manage notes, send updates",
    integrations: ["email_resend", "notes_notion"],
    suggestedPersona: "A creative writer who crafts clear, engaging content and keeps documentation organized.",
  },
  {
    id: "devops",
    name: "DevOps",
    icon: "🔧",
    description: "Monitor repos, deploy, alert the team",
    integrations: ["skills_github", "slack"],
    suggestedPersona: "A reliable DevOps engineer who monitors systems, manages deployments, and keeps the team informed.",
  },
  {
    id: "support",
    name: "Support",
    icon: "🎧",
    description: "Handle customer messages across channels",
    integrations: ["phone_sendblue", "discord"],
    suggestedPersona: "A friendly support agent who responds promptly and professionally across all communication channels.",
  },
  {
    id: "fullstack",
    name: "Full Stack",
    icon: "🚀",
    description: "Maximum capability for any task",
    integrations: [
      "computer_kernel",
      "phone_sendblue",
      "email_resend",
      "skills_github",
      "slack",
      "discord",
      "notes_notion",
      "calendar_google",
    ],
    suggestedPersona: "A versatile agent with full access to all tools, ready to tackle any challenge.",
  },
];

interface ResourceDefinition {
  type: ResourceType;
  icon: string;
  name: string;
  description: string;
  category: "communication" | "computer" | "knowledge" | "workspace";
  priority: "essential" | "recommended" | "optional";
  setupHint: string;
}

const RESOURCE_DEFINITIONS: ResourceDefinition[] = [
  // Essential
  {
    type: "computer_kernel",
    icon: "💻",
    name: "Cloud Computer",
    description: "So they can browse the web, run code, and use apps",
    category: "computer",
    priority: "essential",
    setupHint: "Connect your Kernel.sh account to give your agent a real computer",
  },

  // Communication
  {
    type: "phone_sendblue",
    icon: "📱",
    name: "Phone Number",
    description: "So they can send and receive text messages",
    category: "communication",
    priority: "recommended",
    setupHint: "Connect SendBlue to give your agent SMS capabilities",
  },
  {
    type: "email_resend",
    icon: "📧",
    name: "Email Account",
    description: "So they can send emails on your behalf",
    category: "communication",
    priority: "recommended",
    setupHint: "Connect Resend to enable email sending",
  },

  // Knowledge
  {
    type: "skills_github",
    icon: "📚",
    name: "Skill Library",
    description: "So they can store and reuse tools they learn",
    category: "knowledge",
    priority: "recommended",
    setupHint: "Connect a GitHub repo where your agent stores learned skills",
  },

  // Workspace
  {
    type: "slack",
    icon: "💬",
    name: "Slack Access",
    description: "So they can message your team",
    category: "workspace",
    priority: "optional",
    setupHint: "Connect your Slack workspace",
  },
  {
    type: "discord",
    icon: "🎮",
    name: "Discord",
    description: "So they can chat in your servers",
    category: "workspace",
    priority: "optional",
    setupHint: "Add your Discord bot token",
  },
  {
    type: "notes_notion",
    icon: "📝",
    name: "Notes",
    description: "So they can document and remember things",
    category: "knowledge",
    priority: "optional",
    setupHint: "Connect Notion for documentation",
  },
  {
    type: "calendar_google",
    icon: "📅",
    name: "Calendar",
    description: "So they can schedule and check availability",
    category: "workspace",
    priority: "optional",
    setupHint: "Connect Google Calendar",
  },
];

@customElement("agent-onboarding")
export class AgentOnboarding extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    css`
      :host {
        display: block;
      }
      
      .onboarding {
        padding: var(--space-6);
        max-width: 800px;
        margin: 0 auto;
      }
      
      .header {
        text-align: center;
        margin-bottom: var(--space-8);
      }
      
      .welcome-icon {
        font-size: 64px;
        margin-bottom: var(--space-4);
      }
      
      .title {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .title .agent-name {
        color: var(--rax-yellow);
      }
      
      .subtitle {
        font-size: var(--font-md);
        color: var(--text-secondary);
        line-height: 1.6;
      }
      
      /* Resource sections */
      .section {
        margin-bottom: var(--space-8);
      }
      
      .section-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
      }
      
      .section-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .section-badge {
        font-size: var(--font-xs);
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-full);
        font-weight: 500;
      }
      
      .section-badge.essential {
        background: var(--error);
        color: white;
      }
      
      .section-badge.recommended {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .section-badge.optional {
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
      }
      
      /* Resource cards */
      .resources-grid {
        display: grid;
        gap: var(--space-4);
      }
      
      .resource-card {
        display: flex;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-5);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        transition: all var(--transition-fast);
      }
      
      .resource-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .resource-card.connected {
        border-color: var(--success);
        background: rgba(74, 222, 128, 0.05);
      }
      
      .resource-icon {
        font-size: 32px;
        flex-shrink: 0;
      }
      
      .resource-content {
        flex: 1;
        min-width: 0;
      }
      
      .resource-name {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .resource-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-3);
      }
      
      .resource-status {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
      }
      
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }
      
      .status-dot.connected {
        background: var(--success);
      }
      
      .status-dot.not_connected {
        background: var(--text-tertiary);
      }
      
      .status-dot.error {
        background: var(--error);
      }
      
      .status-dot.connecting {
        background: var(--rax-yellow);
        animation: pulse 1s infinite;
      }
      
      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
      
      .status-text {
        color: var(--text-tertiary);
      }
      
      .status-text.connected {
        color: var(--success);
      }
      
      .status-text.error {
        color: var(--error);
      }
      
      .resource-actions {
        flex-shrink: 0;
      }
      
      .connect-btn {
        padding: var(--space-2) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .connect-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .connect-btn.connected {
        background: rgba(74, 222, 128, 0.1);
        border-color: var(--success);
        color: var(--success);
      }
      
      /* Footer actions */
      .footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: var(--space-6);
        border-top: 1px solid var(--border-color);
        margin-top: var(--space-8);
      }
      
      .skip-link {
        color: var(--text-tertiary);
        font-size: var(--font-sm);
        cursor: pointer;
        transition: color var(--transition-fast);
      }
      
      .skip-link:hover {
        color: var(--text-secondary);
      }
      
      .progress-info {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      .progress-bar {
        width: 120px;
        height: 6px;
        background: var(--bg-elevated);
        border-radius: var(--radius-full);
        overflow: hidden;
      }
      
      .progress-fill {
        height: 100%;
        background: var(--rax-yellow);
        transition: width var(--transition-base);
      }
      
      /* Playbook section */
      .playbook-section {
        margin-bottom: var(--space-8);
        padding: var(--space-5);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }

      .playbook-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .playbook-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
      }

      .playbook-subtitle {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-top: var(--space-1);
      }

      .playbook-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: var(--space-4);
      }

      .playbook-card {
        display: flex;
        flex-direction: column;
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: all var(--transition-fast);
        min-height: 160px;
      }

      .playbook-card:hover {
        border-color: var(--rax-yellow);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .playbook-card.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
        box-shadow: 0 0 0 1px var(--rax-yellow);
      }

      .playbook-card-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-3);
      }

      .playbook-icon {
        font-size: 28px;
        flex-shrink: 0;
      }

      .playbook-name {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
      }

      .playbook-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.5;
        flex: 1;
        margin-bottom: var(--space-3);
      }

      .playbook-integrations {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        margin-top: auto;
      }

      .integration-badge {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-2);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }

      .integration-badge.highlighted {
        background: rgba(253, 224, 71, 0.1);
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      .integration-badge-icon {
        font-size: 12px;
      }

      .playbook-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border-color);
      }

      .apply-playbook-btn {
        padding: var(--space-2) var(--space-4);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .apply-playbook-btn:hover {
        filter: brightness(1.1);
        transform: translateY(-1px);
      }

      .apply-playbook-btn:disabled {
        background: var(--bg-elevated);
        color: var(--text-tertiary);
        cursor: not-allowed;
        transform: none;
      }

      .playbook-count {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      /* Resource card highlighting when playbook is selected */
      .resource-card.playbook-included {
        border-color: var(--rax-yellow);
        background: rgba(253, 224, 71, 0.05);
      }

      .resource-card.playbook-included .resource-name::after {
        content: " (in playbook)";
        font-size: var(--font-xs);
        font-weight: normal;
        color: var(--rax-yellow);
        margin-left: var(--space-2);
      }
    `,
  ];

  @property({ type: String })
  agentId = "";

  @property({ type: String })
  agentName = "Your Agent";

  @property({ type: Array })
  resources: ResourceConfig[] = [];

  @property({ type: Array })
  playbooks: Playbook[] = [];

  @state()
  private selectedPlaybook: Playbook | null = null;

  /**
   * Get combined playbooks: custom ones passed in + preset playbooks
   */
  private get allPlaybooks(): Playbook[] {
    // Custom playbooks passed via property take precedence
    if (this.playbooks.length > 0) {
      return this.playbooks;
    }
    // Default to preset playbooks
    return PRESET_PLAYBOOKS;
  }

  /**
   * Check if a resource type is part of the selected playbook
   */
  private isInSelectedPlaybook(type: ResourceType): boolean {
    if (!this.selectedPlaybook) return false;
    return this.selectedPlaybook.integrations.includes(type);
  }

  override render() {
    const essential = RESOURCE_DEFINITIONS.filter((r) => r.priority === "essential");
    const recommended = RESOURCE_DEFINITIONS.filter((r) => r.priority === "recommended");
    const optional = RESOURCE_DEFINITIONS.filter((r) => r.priority === "optional");

    const connectedCount = this.resources.filter((r) => r.status === "connected").length;
    const totalCount = RESOURCE_DEFINITIONS.length;
    const progress = (connectedCount / totalCount) * 100;

    return html`
      <div class="onboarding">
        <div class="header">
          <div class="welcome-icon">🎉</div>
          <h1 class="title">
            Welcome <span class="agent-name">${this.agentName}</span>
          </h1>
          <p class="subtitle">
            Let's get them set up with the tools they need.<br>
            Think of this like onboarding a new employee.
          </p>
        </div>

        ${this.renderPlaybooks()}

        <div class="section">
          <div class="section-header">
            <h2 class="section-title">🔴 Essential</h2>
            <span class="section-badge essential">Required for useful work</span>
          </div>
          <div class="resources-grid">
            ${essential.map((r) => this.renderResourceCard(r))}
          </div>
        </div>

        <div class="section">
          <div class="section-header">
            <h2 class="section-title">⚡ Recommended</h2>
            <span class="section-badge recommended">Highly useful</span>
          </div>
          <div class="resources-grid">
            ${recommended.map((r) => this.renderResourceCard(r))}
          </div>
        </div>

        <div class="section">
          <div class="section-header">
            <h2 class="section-title">💡 Optional</h2>
            <span class="section-badge optional">Nice to have</span>
          </div>
          <div class="resources-grid">
            ${optional.map((r) => this.renderResourceCard(r))}
          </div>
        </div>

        <div class="footer">
          <span class="skip-link" @click=${this.onSkip}>
            Skip for now →
          </span>
          <div class="progress-info">
            <span>${connectedCount} of ${totalCount} connected</span>
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${progress}%"></div>
            </div>
          </div>
          <button class="btn btn-primary" @click=${this.onComplete}>
            Complete Onboarding
          </button>
        </div>
      </div>
    `;
  }

  private renderPlaybooks() {
    const playbooks = this.allPlaybooks;

    return html`
      <div class="playbook-section">
        <div class="playbook-header">
          <div>
            <div class="playbook-title">Quick Setup Playbooks</div>
            <div class="playbook-subtitle">
              Choose a role-based bundle to quickly configure integrations
            </div>
          </div>
        </div>
        <div class="playbook-grid">
          ${playbooks.map((p) => this.renderPlaybookCard(p))}
        </div>
        ${this.selectedPlaybook
          ? html`
            <div class="playbook-actions">
              <span class="playbook-count">
                ${this.selectedPlaybook.integrations.length} integrations will be set up
              </span>
              <button
                class="apply-playbook-btn"
                @click=${this.applySelectedPlaybook}
              >
                Apply ${this.selectedPlaybook.name} Playbook
              </button>
            </div>
          `
          : ""}
      </div>
    `;
  }

  private renderPlaybookCard(playbook: Playbook) {
    const isSelected = this.selectedPlaybook?.id === playbook.id;

    return html`
      <div
        class="playbook-card ${isSelected ? "selected" : ""}"
        @click=${() => this.selectPlaybook(playbook)}
      >
        <div class="playbook-card-header">
          <span class="playbook-icon">${playbook.icon}</span>
          <span class="playbook-name">${playbook.name}</span>
        </div>
        <div class="playbook-description">${playbook.description}</div>
        <div class="playbook-integrations">
          ${playbook.integrations.slice(0, 4).map((type) => {
            const def = RESOURCE_DEFINITIONS.find((r) => r.type === type);
            if (!def) return "";
            return html`
              <span class="integration-badge ${isSelected ? "highlighted" : ""}">
                <span class="integration-badge-icon">${def.icon}</span>
                ${def.name}
              </span>
            `;
          })}
          ${playbook.integrations.length > 4
            ? html`
              <span class="integration-badge">
                +${playbook.integrations.length - 4} more
              </span>
            `
            : ""}
        </div>
      </div>
    `;
  }

  private renderResourceCard(def: ResourceDefinition) {
    const config = this.resources.find((r) => r.type === def.type);
    const status = config?.status || "not_connected";
    const isConnected = status === "connected";
    const isInPlaybook = this.isInSelectedPlaybook(def.type);

    const cardClasses = [
      "resource-card",
      isConnected ? "connected" : "",
      isInPlaybook ? "playbook-included" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return html`
      <div class="${cardClasses}">
        <span class="resource-icon">${def.icon}</span>
        <div class="resource-content">
          <div class="resource-name">${def.name}</div>
          <div class="resource-description">${def.description}</div>
          <div class="resource-status">
            <span class="status-dot ${status}"></span>
            <span class="status-text ${status}">
              ${this.getStatusText(status)}
            </span>
          </div>
        </div>
        <div class="resource-actions">
          <button
            class="connect-btn ${isConnected ? "connected" : ""}"
            @click=${() => this.onConnect(def.type)}
          >
            ${isConnected ? "Connected" : "Connect"}
          </button>
        </div>
      </div>
    `;
  }

  private getStatusText(status: ResourceStatus): string {
    switch (status) {
      case "connected":
        return "Ready to use";
      case "connecting":
        return "Connecting...";
      case "error":
        return "Connection failed";
      default:
        return "Not connected";
    }
  }

  private selectPlaybook(playbook: Playbook) {
    // Toggle selection if clicking the same playbook
    if (this.selectedPlaybook?.id === playbook.id) {
      this.selectedPlaybook = null;
      this.dispatchEvent(
        new CustomEvent("playbook-deselected", {
          detail: { playbook },
          bubbles: true,
        }),
      );
    } else {
      this.selectedPlaybook = playbook;
      this.dispatchEvent(
        new CustomEvent("playbook-selected", {
          detail: {
            playbook,
            integrations: playbook.integrations,
            suggestedPersona: playbook.suggestedPersona,
          },
          bubbles: true,
        }),
      );
    }
  }

  private applySelectedPlaybook() {
    if (!this.selectedPlaybook) return;

    this.dispatchEvent(
      new CustomEvent("apply-playbook", {
        detail: {
          playbook: this.selectedPlaybook,
          integrations: this.selectedPlaybook.integrations,
          suggestedPersona: this.selectedPlaybook.suggestedPersona,
        },
        bubbles: true,
      }),
    );

    // Trigger connect for each integration in the playbook
    for (const type of this.selectedPlaybook.integrations) {
      this.dispatchEvent(
        new CustomEvent("connect-resource", {
          detail: { type, fromPlaybook: this.selectedPlaybook.id },
          bubbles: true,
        }),
      );
    }
  }

  private createNewPlaybook() {
    this.dispatchEvent(new CustomEvent("create-playbook", { bubbles: true }));
  }

  private onConnect(type: ResourceType) {
    this.dispatchEvent(
      new CustomEvent("connect-resource", {
        detail: { type },
        bubbles: true,
      }),
    );
  }

  private onSkip() {
    this.dispatchEvent(new CustomEvent("skip-onboarding", { bubbles: true }));
  }

  private onComplete() {
    const connectedResources = this.resources
      .filter((r) => r.status === "connected")
      .map((r) => r.type);

    this.dispatchEvent(
      new CustomEvent("onboarding-complete", {
        detail: {
          agentId: this.agentId,
          resources: connectedResources,
          saveAsPlaybook: false, // TODO: prompt user
        },
        bubbles: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "agent-onboarding": AgentOnboarding;
  }
}
